import React, { useEffect, useMemo, useState } from "react";
import { enqueueSnackbar } from "notistack";
import getAdminSettings from "../../../controllers/settings/getAdminSettings";
import updateAdminSettings from "../../../controllers/settings/updateAdminSettings";
import getCurrencies from "../../../controllers/settings/currencies/getCurrencies";
import getDefaultFeeDocument from "../../../controllers/settings/finance/getDefaultFeeDocument";
import updateDefaultFeeFields from "../../../controllers/settings/finance/updateDefaultFeeFields";
import CardList from "../fees/FlightCard";
import Loading from "../../animation/loading";

function feeDocAmount(row) {
  if (row == null) return "";
  if (typeof row === "object" && row !== null && "value" in row) return row.value;
  return row;
}

function currencyCodeOf(row) {
  return String(row?.code || row?.currency || "")
    .trim()
    .toUpperCase();
}

function renameMapKey(map, fromKey, toKey) {
  if (fromKey === toKey) return map;
  const next = { ...map };
  const value = next[fromKey];
  delete next[fromKey];
  if (toKey) next[toKey] = value;
  return next;
}

function fee(data, key) {
  const raw = data?.[key];
  if (raw == null) return "";
  if (typeof raw === "object" && raw !== null && "value" in raw) return raw.value;
  return raw;
}

function Section({ title, sub, children, action }) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[15px] font-bold text-[#0F172A]">{title}</h3>
          {sub ? (
            <p className="text-[12px] text-[#6D7C94] mt-0.5 leading-relaxed">
              {sub}
            </p>
          ) : null}
        </div>
        {action || null}
      </div>
      {children}
    </section>
  );
}

function OutlineButton({ children, onClick, disabled, primary }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`min-h-[36px] rounded-lg border-[1.5px] px-3 text-[12.5px] font-bold disabled:opacity-50 ${
        primary
          ? "border-primary1 bg-primary1 text-white hover:bg-primary1/90"
          : "border-[#D6DEEA] bg-white text-[#0F172A] hover:bg-[#F8FAFC]"
      }`}
    >
      {children}
    </button>
  );
}

function CurrencySelect({
  value,
  onChange,
  options,
  usedCodes = [],
  disabled,
  className = "",
}) {
  const available = options.filter(
    (code) => code === value || !usedCodes.includes(code),
  );
  const list =
    value && !available.includes(value) ? [value, ...available] : available;

  return (
    <select
      value={value || ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className={`rounded-lg border border-[#D6DEEA] bg-white px-3 py-2 text-[13px] font-bold uppercase outline-none focus:border-primary1 focus:ring-2 focus:ring-primary1/15 disabled:opacity-50 ${className}`}
    >
      <option value="" disabled>
        Select currency
      </option>
      {list.map((code) => (
        <option key={code} value={code}>
          {code}
        </option>
      ))}
    </select>
  );
}

function onlyNumericText(raw) {
  const cleaned = String(raw ?? "").replace(/[^\d.]/g, "");
  const parts = cleaned.split(".");
  if (parts.length <= 1) return cleaned;
  return `${parts[0]}.${parts.slice(1).join("")}`;
}

function clampPercentageText(raw) {
  const cleaned = onlyNumericText(raw);
  if (cleaned === "" || cleaned === ".") return cleaned;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return "";
  if (n > 100) return "100";
  return cleaned;
}

function withdrawalDraftFromSettings(data) {
  const raw = String(data?.withdrawalFeeMethod || "").toLowerCase();
  // Prefer Percentage when unset / unknown — Fixed only when explicitly set.
  const method = raw === "fixed" ? "Fixed" : "Percentage";
  const pctRaw = fee(data, "withdrawalFeePercentage");
  const pctNum = Number(pctRaw);
  return {
    method,
    flat: fee(data, "withdrawalFee"),
    percentage: Number.isFinite(pctNum) && pctNum > 100 ? "100" : pctRaw,
    cap: fee(data, "withdrawalFeeCap"),
  };
}

/** Percentage (primary) or Fixed flat fee. Lives outside parent for focus. */
function WithdrawalFeeEditor({ draft, setDraft, onSave, busy, saving }) {
  const isPercentage = draft.method === "Percentage";
  const inputClass =
    "w-full rounded-lg border border-[#D6DEEA] px-3 py-2 text-[13px] font-semibold tabular-nums outline-none focus:border-primary1 focus:ring-2 focus:ring-primary1/15";

  return (
    <div className="rounded-xl border border-[#E3E8F0] bg-white overflow-hidden">
      <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 px-4 py-3.5 border-b border-[#EEF1F6]">
        <span className="text-[12px] font-semibold text-[#6D7C94] self-center">
          Method
        </span>
        <div className="flex gap-2">
          {["Percentage", "Fixed"].map((m) => (
            <button
              key={m}
              type="button"
              disabled={busy}
              onClick={() => setDraft((prev) => ({ ...prev, method: m }))}
              className={`min-h-[36px] rounded-lg border-[1.5px] px-3 text-[12.5px] font-bold disabled:opacity-50 ${
                draft.method === m
                  ? "border-primary1 bg-primary1 text-white"
                  : "border-[#D6DEEA] bg-white text-[#0F172A] hover:bg-[#F8FAFC]"
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {isPercentage ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 px-4 py-3.5 border-b border-[#EEF1F6]">
            <label className="text-[12px] font-semibold text-[#6D7C94] self-center">
              Percentage
            </label>
            <div className="relative max-w-xs">
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                disabled={busy}
                className={`${inputClass} pr-8`}
                value={draft.percentage ?? ""}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    percentage: clampPercentageText(e.target.value),
                  }))
                }
                placeholder="0–100"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] font-semibold text-[#6D7C94]">
                %
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 px-4 py-3.5 border-b border-[#EEF1F6]">
            <label className="text-[12px] font-semibold text-[#6D7C94] self-center">
              Fee cap
            </label>
            <div className="relative max-w-xs">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[12px] font-semibold text-[#6D7C94]">
                ₦
              </span>
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                disabled={busy}
                className={`${inputClass} pl-7`}
                value={draft.cap ?? ""}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    cap: onlyNumericText(e.target.value),
                  }))
                }
                placeholder="Max charge"
              />
            </div>
          </div>
          <p className="px-4 py-2 text-[11px] text-[#6D7C94]">
            Fee is {draft.percentage || "0"}% of the payout (max 100%), never
            more than the cap, rounded to 2 decimals. Both percentage and cap
            must be above 0.
          </p>
        </>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 px-4 py-3.5 border-b border-[#EEF1F6]">
          <label className="text-[12px] font-semibold text-[#6D7C94] self-center">
            Flat fee
          </label>
          <div className="relative max-w-xs">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[12px] font-semibold text-[#6D7C94]">
              ₦
            </span>
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              disabled={busy}
              className={`${inputClass} pl-7`}
              value={draft.flat ?? ""}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  flat: onlyNumericText(e.target.value),
                }))
              }
            />
          </div>
        </div>
      )}

      <div className="px-4 py-3 flex justify-end">
        <OutlineButton primary disabled={busy} onClick={onSave}>
          {saving ? "Saving…" : "Save"}
        </OutlineButton>
      </div>
    </div>
  );
}

/** Must live outside the parent — inner components remount and steal focus. */
function CurrencyAmountMapEditor({
  rows,
  draft,
  setDraft,
  usedCodes,
  amountHeader,
  emptyText,
  onSave,
  currencyOptions,
  busy,
  saving,
}) {
  return (
    <div className="rounded-xl border border-[#E3E8F0] bg-white overflow-hidden">
      <div className="hidden sm:grid grid-cols-[140px_1fr_88px] gap-3 px-4 py-2.5 border-b border-[#EEF1F6] bg-[#F8FAFC]">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-[#6D7C94]">
          Currency
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-wide text-[#6D7C94]">
          {amountHeader}
        </span>
        <span />
      </div>
      <div className="divide-y divide-[#EEF1F6]">
        {rows.length ? (
          rows.map(([currency, value]) => (
            <div
              key={`${amountHeader}-${currency}`}
              className="grid grid-cols-1 sm:grid-cols-[140px_1fr_88px] gap-2 sm:gap-3 items-center px-4 py-3"
            >
              <CurrencySelect
                value={currency}
                options={currencyOptions}
                usedCodes={usedCodes}
                disabled={busy}
                onChange={(nextCode) =>
                  setDraft((prev) => renameMapKey(prev, currency, nextCode))
                }
              />
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                className="rounded-lg border border-[#D6DEEA] px-3 py-2 text-[13px] font-semibold tabular-nums outline-none focus:border-primary1 focus:ring-2 focus:ring-primary1/15"
                value={value ?? ""}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    [currency]: onlyNumericText(e.target.value),
                  }))
                }
              />
              <button
                type="button"
                className="text-[12.5px] font-semibold text-[#B42318] justify-self-end"
                onClick={() => {
                  const next = { ...draft };
                  delete next[currency];
                  setDraft(next);
                }}
              >
                Remove
              </button>
            </div>
          ))
        ) : (
          <p className="px-4 py-6 text-[13px] text-[#6D7C94]">{emptyText}</p>
        )}
      </div>
      <div className="px-4 py-3 border-t border-[#EEF1F6] flex justify-end">
        <OutlineButton primary disabled={busy} onClick={onSave}>
          {saving ? "Saving…" : "Save map"}
        </OutlineButton>
      </div>
    </div>
  );
}

/**
 * Admin wallet / payout settings from GET|PATCH /payment/v1/setting/admin.
 * Also on Fees → Wallet. Same component both places.
 */
export default function WalletPayoutSettings() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({});
  const [feeDoc, setFeeDoc] = useState(null);
  const [minWithdrawDraft, setMinWithdrawDraft] = useState({});
  const [minBalanceDraft, setMinBalanceDraft] = useState({});
  const [processingDraft, setProcessingDraft] = useState({});
  const [withdrawalFeeDraft, setWithdrawalFeeDraft] = useState(
    withdrawalDraftFromSettings({}),
  );
  const [currencyOptions, setCurrencyOptions] = useState(["NGN"]);

  const load = async () => {
    setLoading(true);
    const [settingsRes, feeRes] = await Promise.all([
      getAdminSettings(),
      getDefaultFeeDocument(),
    ]).finally(() => setLoading(false));

    if (settingsRes?.return && settingsRes?.data) {
      setSettings(settingsRes.data);
      setMinWithdrawDraft(settingsRes.data.minimumWithdrawal || { NGN: 10000 });
      setMinBalanceDraft(settingsRes.data.minimumWalletBalance || {});
      setProcessingDraft(settingsRes.data.withdrawalProcessing || {});
      setWithdrawalFeeDraft(withdrawalDraftFromSettings(settingsRes.data));
    } else if (settingsRes?.msg) {
      enqueueSnackbar(settingsRes.msg, { variant: "error" });
    }

    if (feeRes?.return) {
      setFeeDoc(feeRes.data || null);
    }
  };

  const loadCurrencies = async () => {
    const res = await getCurrencies();
    const rows = res?.return
      ? Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
          ? res.data.data
          : []
      : [];
    // Exchange rates are vs NGN, so NGN is never on that list — add it.
    setCurrencyOptions(
      [...new Set(["NGN", ...rows.map(currencyCodeOf).filter(Boolean)])].sort(),
    );
  };

  useEffect(() => {
    load();
    loadCurrencies();
  }, []);

  const busy = loading || saving;
  const usedMinCodes = useMemo(
    () => Object.keys(minWithdrawDraft || {}).filter(Boolean),
    [minWithdrawDraft],
  );
  const usedMinBalanceCodes = useMemo(
    () => Object.keys(minBalanceDraft || {}).filter(Boolean),
    [minBalanceDraft],
  );
  const usedProcessingCodes = useMemo(
    () => Object.keys(processingDraft || {}).filter(Boolean),
    [processingDraft],
  );
  const nextUnusedCurrency = (used) =>
    currencyOptions.find((code) => !used.includes(code)) || "";

  const saveField = async (payload) => {
    setSaving(true);
    const res = await updateAdminSettings(payload).finally(() =>
      setSaving(false),
    );
    if (res?.return) {
      enqueueSnackbar("Saved", { variant: "success" });
      await load();
    } else {
      enqueueSnackbar(res?.msg || "Could not save", { variant: "error" });
    }
  };

  const saveWithdrawalFee = async () => {
    const method =
      withdrawalFeeDraft.method === "Percentage" ? "Percentage" : "Fixed";
    if (method === "Percentage") {
      const percentage = Number(withdrawalFeeDraft.percentage);
      const cap = Number(withdrawalFeeDraft.cap);
      if (!(percentage > 0) || !(cap > 0)) {
        enqueueSnackbar(
          "Percentage mode needs a percentage above 0 and a fee cap above 0.",
          { variant: "warning" },
        );
        return;
      }
      if (percentage > 100) {
        enqueueSnackbar("Percentage must be between 0 and 100.", {
          variant: "warning",
        });
        return;
      }
      await saveField({
        withdrawalFeeMethod: "Percentage",
        withdrawalFeePercentage: percentage,
        withdrawalFeeCap: cap,
      });
      return;
    }
    const flat = Number(withdrawalFeeDraft.flat);
    if (!Number.isFinite(flat) || flat < 0) {
      enqueueSnackbar("Enter a valid flat withdrawal fee.", {
        variant: "warning",
      });
      return;
    }
    await saveField({
      withdrawalFeeMethod: "Fixed",
      withdrawalFee: flat,
    });
  };

  const saveFeeDocField = async (field, amount) => {
    if (!feeDoc?._id) {
      enqueueSnackbar("No default fee document found to update.", {
        variant: "error",
      });
      return;
    }
    setSaving(true);
    const res = await updateDefaultFeeFields(feeDoc._id, {
      [field]: { method: "Fixed", value: Number(amount) },
    }).finally(() => setSaving(false));
    if (res?.return) {
      enqueueSnackbar("Saved", { variant: "success" });
      await load();
    } else {
      enqueueSnackbar(res?.msg || "Could not save", { variant: "error" });
    }
  };

  const cleanCurrencyAmountMap = (draft) => {
    const cleaned = {};
    Object.entries(draft || {}).forEach(([currency, value]) => {
      const code = String(currency || "")
        .trim()
        .toUpperCase();
      if (!code) return;
      const n = Number(value);
      if (!Number.isFinite(n)) return;
      cleaned[code] = n;
    });
    return cleaned;
  };

  const saveMinimumWithdrawal = async () => {
    await saveField({
      minimumWithdrawal: cleanCurrencyAmountMap(minWithdrawDraft),
    });
  };

  const saveMinimumWalletBalance = async () => {
    await saveField({
      minimumWalletBalance: cleanCurrencyAmountMap(minBalanceDraft),
    });
  };

  const saveWithdrawalProcessing = async () => {
    const cleaned = {};
    Object.entries(processingDraft || {}).forEach(([currency, row]) => {
      const code = String(currency || "")
        .trim()
        .toUpperCase();
      const summary = String(row?.summary || "").trim();
      if (!code || !summary) return;
      const entry = {
        summary,
        businessDaysOnly: Boolean(row?.businessDaysOnly),
      };
      const cutoff = String(row?.cutoff || "").trim();
      const timezone = String(row?.timezone || "").trim();
      if (cutoff) entry.cutoff = cutoff;
      if (timezone) entry.timezone = timezone;
      cleaned[code] = entry;
    });
    await saveField({ withdrawalProcessing: cleaned });
  };

  const paymentMethodFeeCards = [
    {
      title: "Card payment fee",
      feename: "cardFee",
      amount: feeDocAmount(feeDoc?.cardFee),
      btn: "Save",
      highlight: true,
      hint: "From the default fee document",
    },
    {
      title: "Bank transfer fee",
      feename: "bankTransferFee",
      amount: feeDocAmount(feeDoc?.bankTransferFee),
      btn: "Save",
      hint: "From the default fee document",
    },
  ];

  const minRows = Object.entries(minWithdrawDraft || {});
  const minBalanceRows = Object.entries(minBalanceDraft || {});
  const processingRows = Object.entries(processingDraft || {});

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h5 className="text-[#0F172A]">Wallet & payout settings</h5>
          <p className="text-[12px] text-[#6D7C94] mt-1">
            Withdrawal rules from admin settings. Card and bank-transfer fees
            update the default fee document.
          </p>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-[12px] text-[#6D7C94]">
            <Loading /> Loading…
          </div>
        ) : null}
      </div>

      <Section
        title="Payment method fees"
        sub="cardFee and bankTransferFee on the default fee document (Update Fee)."
      >
        <CardList
          cards={paymentMethodFeeCards}
          handleSetFee={saveFeeDocField}
          loading={busy}
        />
        {!loading && !feeDoc?._id ? (
          <p className="text-[12px] text-[#B42318]">
            No default fee document found — create one under subscription fees
            first.
          </p>
        ) : null}
      </Section>

      <Section
        title="Withdrawal fee"
        sub="Percentage of the payout by default (never more than the fee cap, max 100%). Fixed flat fee is still available. Calculated when the payout is requested."
      >
        <WithdrawalFeeEditor
          draft={withdrawalFeeDraft}
          setDraft={setWithdrawalFeeDraft}
          onSave={saveWithdrawalFee}
          busy={busy}
          saving={saving}
        />
      </Section>

      <Section
        title="Minimum withdrawal"
        sub="Per wallet currency. Saving replaces the whole map. Unlisted currencies have no minimum."
        action={
          <OutlineButton
            disabled={busy || !nextUnusedCurrency(usedMinCodes)}
            onClick={() => {
              const code = nextUnusedCurrency(usedMinCodes);
              if (!code) {
                enqueueSnackbar("All currencies are already listed.", {
                  variant: "warning",
                });
                return;
              }
              setMinWithdrawDraft((prev) => ({ ...prev, [code]: "" }));
            }}
          >
            Add currency
          </OutlineButton>
        }
      >
        <CurrencyAmountMapEditor
          rows={minRows}
          draft={minWithdrawDraft}
          setDraft={setMinWithdrawDraft}
          usedCodes={usedMinCodes}
          amountHeader="Minimum payout"
          emptyText="No minimums yet. Add a currency."
          onSave={saveMinimumWithdrawal}
          currencyOptions={currencyOptions}
          busy={busy}
          saving={saving}
        />
      </Section>

      <Section
        title="Minimum wallet balance"
        sub="Balance that must remain after a payout, per currency. Saving replaces the whole map. Unlisted currencies have no floor. Payouts that would leave less return 400 WITHDRAWAL_BELOW_REMAINING_BALANCE."
        action={
          <OutlineButton
            disabled={busy || !nextUnusedCurrency(usedMinBalanceCodes)}
            onClick={() => {
              const code = nextUnusedCurrency(usedMinBalanceCodes);
              if (!code) {
                enqueueSnackbar("All currencies are already listed.", {
                  variant: "warning",
                });
                return;
              }
              setMinBalanceDraft((prev) => ({ ...prev, [code]: "" }));
            }}
          >
            Add currency
          </OutlineButton>
        }
      >
        <CurrencyAmountMapEditor
          rows={minBalanceRows}
          draft={minBalanceDraft}
          setDraft={setMinBalanceDraft}
          usedCodes={usedMinBalanceCodes}
          amountHeader="Min remaining"
          emptyText="No floor yet. Add a currency (default is none)."
          onSave={saveMinimumWalletBalance}
          currencyOptions={currencyOptions}
          busy={busy}
          saving={saving}
        />
      </Section>

      <Section
        title="Withdrawal processing"
        sub="Copy shown to agents per currency. Summary is required. Cutoff (HH:mm) needs a valid IANA timezone."
        action={
          <OutlineButton
            disabled={busy || !nextUnusedCurrency(usedProcessingCodes)}
            onClick={() => {
              const code = nextUnusedCurrency(usedProcessingCodes);
              if (!code) {
                enqueueSnackbar("All currencies are already listed.", {
                  variant: "warning",
                });
                return;
              }
              setProcessingDraft((prev) => ({
                ...prev,
                [code]: {
                  summary: "",
                  cutoff: "",
                  timezone: "",
                  businessDaysOnly: true,
                },
              }));
            }}
          >
            Add currency
          </OutlineButton>
        }
      >
        <div className="flex flex-col gap-3">
          {processingRows.length ? (
            processingRows.map(([currency, row]) => (
              <div
                key={`proc-${currency}`}
                className="rounded-xl border border-[#E3E8F0] bg-white p-4 flex flex-col gap-3"
              >
                <div className="flex items-center gap-2">
                  <CurrencySelect
                    className="w-36"
                    value={currency}
                    options={currencyOptions}
                    usedCodes={usedProcessingCodes}
                    disabled={busy}
                    onChange={(nextCode) =>
                      setProcessingDraft((prev) =>
                        renameMapKey(prev, currency, nextCode),
                      )
                    }
                  />
                  <button
                    type="button"
                    className="ml-auto text-[12.5px] font-semibold text-[#B42318]"
                    onClick={() => {
                      const next = { ...processingDraft };
                      delete next[currency];
                      setProcessingDraft(next);
                    }}
                  >
                    Remove
                  </button>
                </div>
                <input
                  className="rounded-lg border border-[#D6DEEA] px-3 py-2 text-[13px] outline-none focus:border-primary1 focus:ring-2 focus:ring-primary1/15"
                  placeholder="Summary (required) — e.g. Within 1 to 2 business days"
                  value={row?.summary || ""}
                  onChange={(e) =>
                    setProcessingDraft((prev) => ({
                      ...prev,
                      [currency]: {
                        ...prev[currency],
                        summary: e.target.value,
                      },
                    }))
                  }
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input
                    className="rounded-lg border border-[#D6DEEA] px-3 py-2 text-[13px] outline-none focus:border-primary1"
                    placeholder="Cutoff HH:mm (optional)"
                    value={row?.cutoff || ""}
                    onChange={(e) =>
                      setProcessingDraft((prev) => ({
                        ...prev,
                        [currency]: {
                          ...prev[currency],
                          cutoff: e.target.value,
                        },
                      }))
                    }
                  />
                  <input
                    className="rounded-lg border border-[#D6DEEA] px-3 py-2 text-[13px] outline-none focus:border-primary1"
                    placeholder="Timezone e.g. Africa/Lagos"
                    value={row?.timezone || ""}
                    onChange={(e) =>
                      setProcessingDraft((prev) => ({
                        ...prev,
                        [currency]: {
                          ...prev[currency],
                          timezone: e.target.value,
                        },
                      }))
                    }
                  />
                </div>
                <label className="flex items-center gap-2.5 text-[13px] text-[#0F172A] w-fit cursor-pointer">
                  <span
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      row?.businessDaysOnly ? "bg-primary1" : "bg-[#D6DEEA]"
                    }`}
                    onClick={() =>
                      setProcessingDraft((prev) => ({
                        ...prev,
                        [currency]: {
                          ...prev[currency],
                          businessDaysOnly: !prev[currency]?.businessDaysOnly,
                        },
                      }))
                    }
                    role="switch"
                    aria-checked={Boolean(row?.businessDaysOnly)}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${
                        row?.businessDaysOnly
                          ? "translate-x-[18px]"
                          : "translate-x-[2px]"
                      }`}
                    />
                  </span>
                  Business days only
                </label>
              </div>
            ))
          ) : (
            <p className="rounded-xl border border-[#E3E8F0] bg-white px-4 py-6 text-[13px] text-[#6D7C94]">
              No processing copy yet. Add a currency.
            </p>
          )}
          <div className="flex justify-end">
            <OutlineButton
              primary
              disabled={busy || !processingRows.length}
              onClick={saveWithdrawalProcessing}
            >
              {saving ? "Saving…" : "Save processing copy"}
            </OutlineButton>
          </div>
        </div>
      </Section>

      <div className="rounded-xl border border-[#E3E8F0] bg-white px-4 py-3.5 flex items-center justify-between gap-3">
        <div>
          <p className="text-[13px] font-bold text-[#0F172A]">Payout OTP</p>
          <p className="text-[12px] text-[#6D7C94] mt-0.5">
            Require OTP when agents withdraw from wallet.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={Boolean(settings?.payoutOTPActive)}
          disabled={busy}
          onClick={() =>
            saveField({ payoutOTPActive: !settings?.payoutOTPActive })
          }
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
            settings?.payoutOTPActive ? "bg-primary1" : "bg-[#D6DEEA]"
          }`}
        >
          <span
            className={`inline-block h-[18px] w-[18px] transform rounded-full bg-white shadow transition ${
              settings?.payoutOTPActive
                ? "translate-x-[22px]"
                : "translate-x-[3px]"
            }`}
          />
        </button>
      </div>
    </div>
  );
}
